import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Settings, Plus, Edit2, Trash2, Eye, EyeOff, Save, X, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const SubjectManager = () => {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [settings, setSettings] = useState({
    subject_enabled: 'true',
    subject_name_singular: 'Pet',
    subject_name_plural: 'Pets',
    subject_required_for_booking: 'true',
    subject_icon: '🐾'
  });
  const [loading, setLoading] = useState(true);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    field_name: '',
    field_label: '',
    field_type: 'text',
    placeholder: '',
    is_required: false,
    is_active: true,
    display_order: 0,
    options: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const [fieldsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/subject-fields`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/subject-fields/settings/config`)
      ]);
      
      console.log('Fields response:', fieldsRes.data);
      console.log('Settings response:', settingsRes.data);
      
      setFields(Array.isArray(fieldsRes.data) ? fieldsRes.data : []);
      setSettings(prev => ({ ...prev, ...(settingsRes.data || {}) }));
      setLoading(false);
    } catch (error) {
      console.error('Load error:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load subject configuration');
      setLoading(false);
    }
  };

  const handleSettingsChange = (key, value) => {
    console.log(`Setting ${key} to:`, value, 'Type:', typeof value);
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      console.log('New settings state:', newSettings);
      return newSettings;
    });
  };

  const saveSettings = async () => {
    try {
      const token = sessionStorage.getItem('token');
      console.log('Saving settings:', settings);
      
      // Base64 encode the emoji to preserve it during transmission
      const settingsToSend = {
        ...settings,
        subject_icon: settings.subject_icon ? btoa(String.fromCharCode(...new TextEncoder().encode(settings.subject_icon))) : ''
      };
      console.log('Encoded icon:', settingsToSend.subject_icon);
      
      await axios.put(`${API_URL}/subject-fields/settings/config`, settingsToSend, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'X-Icon-Encoded': 'true' // Signal that icon is base64 encoded
        }
      });
      toast.success('Settings saved successfully!');
      await loadData();
    } catch (error) {
      console.error('Save settings error:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to save settings');
    }
  };

  const openFieldModal = (field = null) => {
    if (field) {
      setEditingField(field);
      setFieldForm({
        field_name: field.field_name,
        field_label: field.field_label,
        field_type: field.field_type,
        placeholder: field.placeholder || '',
        is_required: field.is_required,
        is_active: field.is_active,
        display_order: field.display_order,
        options: field.options || []
      });
    } else {
      setEditingField(null);
      setFieldForm({
        field_name: '',
        field_label: '',
        field_type: 'text',
        placeholder: '',
        is_required: false,
        is_active: true,
        display_order: fields.length,
        options: []
      });
    }
    setShowFieldModal(true);
  };

  const closeFieldModal = () => {
    setShowFieldModal(false);
    setEditingField(null);
  };

  const handleFieldFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFieldForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveField = async (e) => {
    e.preventDefault();
    
    try {
      const token = sessionStorage.getItem('token');
      
      if (editingField) {
        await axios.put(`${API_URL}/subject-fields/${editingField.id}`, fieldForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Field updated successfully!');
      } else {
        await axios.post(`${API_URL}/subject-fields`, fieldForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Field created successfully!');
      }
      
      closeFieldModal();
      loadData();
    } catch (error) {
      console.error('Save field error:', error);
      toast.error(error.response?.data?.error || 'Failed to save field');
    }
  };

  const toggleFieldActive = async (fieldId) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(`${API_URL}/subject-fields/${fieldId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field status updated!');
      loadData();
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('Failed to update field status');
    }
  };

  const toggleFieldRequired = async (fieldId) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(`${API_URL}/subject-fields/${fieldId}/toggle-required`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field requirement updated!');
      loadData();
    } catch (error) {
      console.error('Toggle required error:', error);
      toast.error('Failed to update field requirement');
    }
  };

  const deleteField = async (fieldId) => {
    if (!window.confirm('Are you sure you want to delete this field? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(`${API_URL}/subject-fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field deleted successfully!');
      loadData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete field');
    }
  };

  const addOption = () => {
    setFieldForm(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const updateOption = (index, value) => {
    setFieldForm(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  const removeOption = (index) => {
    setFieldForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  const isEnabled = settings.subject_enabled === 'true';

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin?tab=settings')}
          className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 mb-4"
        >
          <ArrowLeft size={20} />
          <span>Back to Settings</span>
        </button>
        <h1 className="text-3xl font-bold mb-2">Service Subject Configuration</h1>
        <p className="text-gray-600">
          Customize what your business services (Pets, Lawns, Vehicles, etc.) or disable subjects entirely
        </p>
      </div>

      {/* System Settings */}
      <div className="card mb-8">
        <div className="flex items-center space-x-3 mb-6">
          <Settings className="text-primary-600" size={24} />
          <h2 className="text-2xl font-bold">System Settings</h2>
        </div>

        <div className="space-y-6">
          {/* Enable/Disable Subjects */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-semibold text-lg">Enable Service Subjects</h3>
              <p className="text-sm text-gray-600">
                Turn off if your business doesn't require specific subjects (e.g., consulting services)
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => handleSettingsChange('subject_enabled', e.target.checked.toString())}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {isEnabled && (
            <>
              {/* Subject Names */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Singular Name (e.g., Pet, Lawn, Vehicle)</label>
                  <input
                    type="text"
                    value={settings.subject_name_singular || ''}
                    onChange={(e) => handleSettingsChange('subject_name_singular', e.target.value)}
                    className="input"
                    placeholder="Pet"
                  />
                </div>
                <div>
                  <label className="label">Plural Name (e.g., Pets, Lawns, Vehicles)</label>
                  <input
                    type="text"
                    value={settings.subject_name_plural || ''}
                    onChange={(e) => handleSettingsChange('subject_name_plural', e.target.value)}
                    className="input"
                    placeholder="Pets"
                  />
                </div>
              </div>

              {/* Icon */}
              <div>
                <label className="label">Icon/Emoji</label>
                <input
                  type="text"
                  value={settings.subject_icon || ''}
                  onChange={(e) => handleSettingsChange('subject_icon', e.target.value)}
                  className="input w-32"
                  placeholder="🐾"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use an emoji or icon to represent subjects in the UI
                </p>
              </div>

              {/* Required for Booking */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="required_booking"
                  checked={settings.subject_required_for_booking === 'true'}
                  onChange={(e) => handleSettingsChange('subject_required_for_booking', e.target.checked.toString())}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
                <label htmlFor="required_booking" className="text-sm font-medium text-gray-700">
                  Require a subject to book appointments
                </label>
              </div>
            </>
          )}

          <div className="flex justify-end">
            <button onClick={saveSettings} className="btn btn-primary flex items-center space-x-2">
              <Save size={18} />
              <span>Save Settings</span>
            </button>
          </div>
        </div>
      </div>

      {/* Subject Fields */}
      {isEnabled && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold">
                {settings.subject_name_singular || 'Subject'} Fields
              </h2>
              <p className="text-gray-600">
                Define what information to collect for each {(settings.subject_name_singular || 'subject').toLowerCase()}
              </p>
            </div>
            <button onClick={() => openFieldModal()} className="btn btn-primary flex items-center space-x-2">
              <Plus size={18} />
              <span>Add Field</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Field Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Label</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fields.map(field => (
                  <tr key={field.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {field.field_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {field.field_label}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {field.field_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleFieldRequired(field.id)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          field.is_required
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        title={`Click to make ${field.is_required ? 'optional' : 'required'}`}
                      >
                        {field.is_required ? 'Required' : 'Optional'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        field.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {field.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => toggleFieldActive(field.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title={field.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {field.is_active ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      <button
                        onClick={() => openFieldModal(field)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => deleteField(field.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Field Modal */}
      {showFieldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold">
                {editingField ? 'Edit Field' : 'Add New Field'}
              </h2>
              <button onClick={closeFieldModal} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSaveField} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Field Name *</label>
                  <input
                    type="text"
                    name="field_name"
                    value={fieldForm.field_name}
                    onChange={handleFieldFormChange}
                    required
                    className="input"
                    placeholder="e.g., breed, size, color"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {editingField 
                      ? '⚠️ Changing this may affect existing data' 
                      : 'Internal name (no spaces)'}
                  </p>
                </div>

                <div>
                  <label className="label">Field Label *</label>
                  <input
                    type="text"
                    name="field_label"
                    value={fieldForm.field_label}
                    onChange={handleFieldFormChange}
                    required
                    className="input"
                    placeholder="e.g., Breed, Size, Color"
                  />
                  <p className="text-xs text-gray-500 mt-1">Display name</p>
                </div>
              </div>

              <div>
                <label className="label">Field Type *</label>
                <select
                  name="field_type"
                  value={fieldForm.field_type}
                  onChange={handleFieldFormChange}
                  className="input"
                >
                  <option value="text">Text</option>
                  <option value="textarea">Text Area</option>
                  <option value="number">Number</option>
                  <option value="email">Email</option>
                  <option value="tel">Phone</option>
                  <option value="date">Date</option>
                  <option value="select">Dropdown</option>
                  <option value="checkbox">Checkbox</option>
                </select>
              </div>

              {(fieldForm.field_type === 'select' || fieldForm.field_type === 'checkbox') && (
                <div>
                  <label className="label">Options</label>
                  {fieldForm.options.map((option, index) => (
                    <div key={index} className="flex space-x-2 mb-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        className="input flex-1"
                        placeholder={`Option ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="btn btn-secondary"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addOption}
                    className="btn btn-secondary flex items-center space-x-2"
                  >
                    <Plus size={16} />
                    <span>Add Option</span>
                  </button>
                </div>
              )}

              <div>
                <label className="label">Placeholder</label>
                <input
                  type="text"
                  name="placeholder"
                  value={fieldForm.placeholder}
                  onChange={handleFieldFormChange}
                  className="input"
                  placeholder="Optional placeholder text"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_required"
                    name="is_required"
                    checked={fieldForm.is_required}
                    onChange={handleFieldFormChange}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <label htmlFor="is_required" className="text-sm font-medium">Required Field</label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={fieldForm.is_active}
                    onChange={handleFieldFormChange}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium">Active</label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={closeFieldModal} className="flex-1 btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn btn-primary">
                  {editingField ? 'Update Field' : 'Create Field'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectManager;
